/**
 * Partner Dashboard for TheGoodMen ops team.
 * Standalone shell — NOT inside AppShell — so it feels co-branded TGM.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ChartBar, Package, ShoppingBag, TrendUp, ArrowsClockwise, SignOut,
  Wrench, ShieldCheck, CurrencyInr, Buildings, ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const err = (e) => toast.error(formatApiError(e?.response?.data?.detail));
const INR = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;
const STATUS_COLOR = {
  pending_approval: "bg-amber-100 text-amber-800", approved: "bg-blue-100 text-blue-700",
  with_partner: "bg-teal-100 text-teal-700", in_progress: "bg-cyan-100 text-cyan-700",
  shipped: "bg-indigo-100 text-indigo-700",
  delivered: "bg-emerald-100 text-emerald-700", resolved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
};

const STAGE_ORDER = ["approved","with_partner","in_progress","shipped","delivered","resolved"];

export default function PartnerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [me, setMe] = useState(null);
  const [overview, setOverview] = useState(null);
  const [requests, setRequests] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [filter, setFilter] = useState({ status: "", type: "", q: "" });

  const load = async () => {
    try {
      const [m, o, r, c] = await Promise.all([
        api.get("/tech/partner/me"),
        api.get("/tech/partner/overview"),
        api.get("/tech/partner/requests"),
        api.get("/tech/partner/catalog"),
      ]);
      setMe(m.data); setOverview(o.data); setRequests(r.data); setCatalog(c.data);
    } catch (e) { err(e); }
  };
  useEffect(() => { load(); }, []);

  const t = overview?.totals || {};
  const filtered = useMemo(() => {
    return requests.filter(r => {
      if (filter.status && r.status !== filter.status) return false;
      if (filter.type && r.request_type !== filter.type) return false;
      if (filter.q && !`${r.title} ${r.requester_name} ${r.company_name} ${r.osticket_number || ""}`.toLowerCase().includes(filter.q.toLowerCase())) return false;
      return true;
    });
  }, [requests, filter]);

  // Permission gate — only partner_admin or super_admin
  if (user && user.role !== "partner_admin" && user.role !== "super_admin") {
    return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500" data-testid="partner-forbidden">This dashboard is for partner operators only.</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="partner-shell">
      {/* TGM-branded top bar */}
      <header className="bg-gradient-to-r from-teal-800 to-emerald-700 text-white sticky top-0 z-30 shadow-md">
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-white text-teal-700 flex items-center justify-center font-black text-lg">G</div>
            <div>
              <div className="text-sm font-bold leading-tight">TheGoodMen · Partner Console</div>
              <div className="text-[10px] text-emerald-100 leading-tight">Powered by SaarOne · IT Service Backbone for India</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20 gap-1.5" onClick={load} data-testid="refresh-btn">
              <ArrowsClockwise size={14}/>Refresh
            </Button>
            <div className="text-xs">
              <div className="text-emerald-100">{me?.name || "TheGoodMen"}</div>
              <div className="opacity-75">{user?.email}</div>
            </div>
            <Button size="sm" variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20 gap-1.5" onClick={() => { logout(); navigate("/login"); }} data-testid="partner-logout">
              <SignOut size={14}/>Logout
            </Button>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-5 flex gap-1">
          {[
            ["overview","Overview", ChartBar],
            ["requests","Requests", Package],
            ["catalog","Catalog", ShoppingBag],
            ["customers","Customers", Buildings],
          ].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`p-tab-${k}`}
              className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5 border-b-2 transition ${
                tab === k ? "border-white text-white" : "border-transparent text-emerald-100 hover:text-white"
              }`}>
              <Icon size={13}/> {label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto p-5">
        {tab === "overview" && overview && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Kpi label="Total requests" value={t.total_requests} color="bg-white"/>
              <Kpi label="In progress" value={t.in_progress} color="bg-cyan-50"/>
              <Kpi label="Delivered" value={t.delivered} color="bg-emerald-50"/>
              <Kpi label="Revenue MTD" value={INR(t.revenue_mtd)} color="bg-amber-50"/>
              <Kpi label="Revenue YTD" value={INR(t.revenue_ytd)} color="bg-violet-50"/>
            </div>
            {t.queued_outbox > 0 && (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-center justify-between" data-testid="outbox-alert">
                <div>
                  <div className="font-semibold text-rose-900">{t.queued_outbox} request(s) queued — osTicket key rejected</div>
                  <div className="text-xs text-rose-700 mt-0.5">SaarOne is retrying every minute. Activate the key in osTicket Admin → Manage → API Keys to flush instantly.</div>
                </div>
                <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-100" onClick={async () => {
                  try { const r = await api.post("/tech/partner/outbox/replay"); toast.success(`Delivered ${r.data.delivered} ticket(s).`); load(); } catch (e) { err(e); }
                }} data-testid="outbox-retry">Retry now</Button>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-white rounded-xl border border-zinc-200 p-5">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><Package className="text-teal-700" size={18}/> Recent activity</h3>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Customer</TableHead><TableHead>Title</TableHead>
                    <TableHead>Amount</TableHead><TableHead>Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(overview.recent || []).slice(0, 10).map(r => (
                      <TableRow key={r.id} className="cursor-pointer hover:bg-zinc-50">
                        <TableCell className="text-xs">{r.company_id?.slice(0,8) || "—"}</TableCell>
                        <TableCell className="text-xs font-medium truncate max-w-[200px]">{r.title}</TableCell>
                        <TableCell className="text-xs tabular-nums">{r.total_amount ? INR(r.total_amount) : "—"}</TableCell>
                        <TableCell><Badge className={STATUS_COLOR[r.status] || "bg-zinc-100"}>{r.status.replace(/_/g," ")}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="bg-white rounded-xl border border-zinc-200 p-5">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><TrendUp className="text-emerald-700" size={18}/> Top customers</h3>
                <div className="space-y-2.5">
                  {(overview.top_customers || []).length === 0 && <div className="text-xs text-zinc-500">No revenue yet.</div>}
                  {(overview.top_customers || []).map((c, i) => (
                    <div key={c.company_id} className="flex items-center gap-3" data-testid={`top-cust-${c.company_id}`}>
                      <div className="w-6 h-6 rounded bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">{i+1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{c.company_name}</div>
                      </div>
                      <div className="text-xs font-semibold tabular-nums">{INR(c.spend)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "requests" && (
          <div className="bg-white rounded-xl border border-zinc-200">
            <div className="p-4 border-b border-zinc-200 flex gap-2 flex-wrap items-center">
              <Input placeholder="Search by name, customer, ticket #…" value={filter.q} onChange={e=>setFilter({...filter, q: e.target.value})} className="w-64 h-9" data-testid="p-search"/>
              <Select value={filter.status || "all"} onValueChange={v => setFilter({...filter, status: v === "all" ? "" : v})}>
                <SelectTrigger className="w-44 h-9 text-xs" data-testid="p-status-filter"><SelectValue placeholder="All statuses"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STAGE_ORDER.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g," ")}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filter.type || "all"} onValueChange={v => setFilter({...filter, type: v === "all" ? "" : v})}>
                <SelectTrigger className="w-32 h-9 text-xs"><SelectValue placeholder="All types"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {["purchase","service","amc","incident"].map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="ml-auto text-xs text-zinc-500">{filtered.length} request{filtered.length !== 1 ? "s" : ""}</div>
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Created</TableHead><TableHead>Customer</TableHead><TableHead>Title / Item</TableHead>
                <TableHead>Type</TableHead><TableHead>Amount</TableHead>
                <TableHead>Status</TableHead><TableHead>TGM #</TableHead><TableHead/>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map(r => <PartnerReqRow key={r.id} r={r} onUpdated={load}/>)}
                {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-500 py-8">No requests match these filters.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}

        {tab === "catalog" && (
          <CatalogManager
            catalog={catalog}
            onChanged={load}
          />
        )}

        {tab === "customers" && (
          <div className="bg-white rounded-xl border border-zinc-200 p-5">
            <h3 className="font-semibold mb-3">Customers</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(overview?.top_customers || []).map(c => (
                <div key={c.company_id} className="border border-zinc-200 rounded-lg p-4 hover:border-teal-400 transition">
                  <div className="font-semibold">{c.company_name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5 font-mono">{c.company_id.slice(0,8)}…</div>
                  <div className="text-lg font-bold mt-2 tabular-nums">{INR(c.spend)}</div>
                  <div className="text-xs text-zinc-500">spend on TheGoodMen</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div className={`${color} border border-zinc-200 rounded-lg p-4`}>
      <div className="text-[11px] uppercase tracking-wide font-semibold text-zinc-500">{label}</div>
      <div className="text-2xl font-bold mt-1 tabular-nums">{value ?? "—"}</div>
    </div>
  );
}

function PartnerReqRow({ r, onUpdated }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow className="hover:bg-zinc-50" data-testid={`p-row-${r.id}`}>
        <TableCell className="text-xs">{new Date(r.created_at).toLocaleDateString()}</TableCell>
        <TableCell className="text-xs font-medium">{r.company_name}</TableCell>
        <TableCell className="text-xs">
          <div className="font-medium">{r.title}</div>
          <div className="text-zinc-500 text-[10px]">{r.requester_name}{r.catalog_item_name ? ` · ${r.catalog_item_name}` : ""}</div>
        </TableCell>
        <TableCell><Badge variant="outline" className="text-[10px] capitalize">{r.request_type}</Badge></TableCell>
        <TableCell className="text-xs tabular-nums">{r.total_amount ? INR(r.total_amount) : "—"}</TableCell>
        <TableCell><Badge className={STATUS_COLOR[r.status] || "bg-zinc-100"}>{r.status.replace(/_/g," ")}</Badge></TableCell>
        <TableCell className="text-xs font-mono">{r.osticket_number || (r.status === "with_partner" ? <span className="text-amber-600">queued</span> : "—")}</TableCell>
        <TableCell><Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`p-manage-${r.id}`}>Manage</Button></TableCell>
      </TableRow>
      {open && <ManageDialog r={r} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onUpdated(); }}/>}
    </>
  );
}

function ManageDialog({ r, onClose, onDone }) {
  const [form, setForm] = useState({
    set_status: r.status, partner_status: r.partner_status || "",
    partner_eta: r.partner_eta || "", partner_technician: r.partner_technician || "", note: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/tech/partner/requests/${r.id}/status`, form);
      toast.success("Status updated. Customer will see this immediately.");
      onDone();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="manage-dialog">
        <DialogHeader>
          <DialogTitle>{r.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="bg-zinc-50 border border-zinc-200 rounded p-3 text-xs">
            <div><span className="text-zinc-500">Customer:</span> <span className="font-medium">{r.company_name}</span></div>
            <div><span className="text-zinc-500">Requester:</span> {r.requester_name} ({r.requester_email})</div>
            {r.catalog_item_name && <div><span className="text-zinc-500">Item:</span> {r.catalog_item_name} × {r.qty}</div>}
            {r.total_amount > 0 && <div><span className="text-zinc-500">Amount:</span> <span className="font-medium">{INR(r.total_amount)}</span></div>}
            {r.osticket_number && <div><span className="text-zinc-500">Ticket:</span> <span className="font-mono">{r.osticket_number}</span></div>}
            {r.description && <div className="mt-2 pt-2 border-t border-zinc-200">{r.description}</div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Master status</Label>
              <Select value={form.set_status} onValueChange={v => setForm({...form, set_status: v})}>
                <SelectTrigger className="text-xs" data-testid="mgmt-status"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {STAGE_ORDER.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g," ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Partner sub-status</Label>
              <Input value={form.partner_status} onChange={e=>setForm({...form, partner_status: e.target.value})} placeholder="e.g. en_route" className="text-xs" data-testid="mgmt-pstatus"/>
            </div>
            <div>
              <Label className="text-xs">ETA</Label>
              <Input value={form.partner_eta} onChange={e=>setForm({...form, partner_eta: e.target.value})} placeholder="e.g. Today 4pm" className="text-xs"/>
            </div>
            <div>
              <Label className="text-xs">Technician</Label>
              <Input value={form.partner_technician} onChange={e=>setForm({...form, partner_technician: e.target.value})} placeholder="e.g. Karthik R · +91-90000-00000" className="text-xs"/>
            </div>
          </div>
          <div>
            <Label className="text-xs">Note to customer</Label>
            <Textarea rows={2} value={form.note} onChange={e=>setForm({...form, note: e.target.value})} className="text-xs" data-testid="mgmt-note"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mgmt-save">{busy ? "Saving…" : "Push update"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ────────────────────────────────────────────────────────────────────
// Catalog Manager — full CRUD for partner's SKUs.
// Categories are seeded from existing rows + a fixed default list.
// ────────────────────────────────────────────────────────────────────
const DEFAULT_CATEGORIES = [
  { key: "laptop",     label: "Laptops" },
  { key: "monitor",    label: "Monitors" },
  { key: "networking", label: "Networking" },
  { key: "printer",    label: "Printers" },
  { key: "accessory",  label: "Accessories" },
  { key: "software",   label: "Software" },
  { key: "service",    label: "Services" },
  { key: "amc_plan",   label: "AMC Plans" },
];

function CatalogManager({ catalog, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [catFilter, setCatFilter] = useState("");
  const [search, setSearch] = useState("");

  const categories = useMemo(() => {
    const seen = new Set(DEFAULT_CATEGORIES.map(c => c.key));
    const extra = [];
    for (const r of catalog) {
      if (r.category && !seen.has(r.category)) {
        seen.add(r.category);
        extra.push({ key: r.category, label: r.category.replace(/_/g," ") });
      }
    }
    return [...DEFAULT_CATEGORIES, ...extra];
  }, [catalog]);

  const counts = useMemo(() => {
    const c = {};
    for (const r of catalog) c[r.category] = (c[r.category] || 0) + 1;
    return c;
  }, [catalog]);

  const filtered = useMemo(() => {
    return catalog.filter(c => {
      if (catFilter && c.category !== catFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!`${c.sku} ${c.name} ${c.brand || ""}`.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [catalog, catFilter, search]);

  const doDelete = async () => {
    try {
      await api.delete(`/tech/catalog/${confirmDelete.id}`);
      toast.success(`Removed "${confirmDelete.name}"`);
      setConfirmDelete(null);
      onChanged();
    } catch (e) { err(e); }
  };

  const restore = async (row) => {
    try {
      await api.put(`/tech/catalog/${row.id}`, { is_active: true });
      toast.success(`Restored "${row.name}"`);
      onChanged();
    } catch (e) { err(e); }
  };

  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-5" data-testid="catalog-manager">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold">Your catalog · {catalog.filter(c=>c.is_active).length} active SKUs</h3>
          <p className="text-xs text-zinc-500">Add, edit and remove items. Changes propagate live to every customer's shop.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search by SKU, name, brand…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-64 text-xs"
            data-testid="catalog-search"
          />
          <Button onClick={() => setEditing("new")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="catalog-add-btn">
            <ShoppingBag size={16} className="mr-1.5"/> Add SKU
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="catalog-cats">
        <button
          onClick={() => setCatFilter("")}
          className={`text-xs px-3 py-1 rounded-full border ${!catFilter ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}
          data-testid="cat-chip-all"
        >
          All · {catalog.length}
        </button>
        {categories.map(c => (
          <button
            key={c.key}
            onClick={() => setCatFilter(c.key)}
            className={`text-xs px-3 py-1 rounded-full border capitalize ${catFilter === c.key ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-zinc-700 border-zinc-200 hover:border-emerald-400"}`}
            data-testid={`cat-chip-${c.key}`}
          >
            {c.label} · {counts[c.key] || 0}
          </button>
        ))}
      </div>

      <Table>
        <TableHeader><TableRow>
          <TableHead>SKU</TableHead><TableHead>Name</TableHead>
          <TableHead>Category</TableHead><TableHead className="text-right">Price</TableHead>
          <TableHead className="text-right">Stock</TableHead><TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {filtered.map(c => (
            <TableRow key={c.id} className={c.is_active ? "" : "opacity-60"}>
              <TableCell className="text-xs font-mono">{c.sku}</TableCell>
              <TableCell className="text-sm font-medium">{c.name}{c.brand ? <span className="text-xs text-zinc-500"> · {c.brand}</span> : null}</TableCell>
              <TableCell className="text-xs capitalize">{c.category.replace(/_/g," ")}</TableCell>
              <TableCell className="text-sm text-right tabular-nums">{INR(c.price)}</TableCell>
              <TableCell className="text-xs text-right tabular-nums">{c.stock_count ?? "—"}</TableCell>
              <TableCell>
                {!c.is_active
                  ? <Badge className="bg-rose-50 text-rose-700">Removed</Badge>
                  : c.in_stock
                    ? <Badge className="bg-emerald-100 text-emerald-700">In stock</Badge>
                    : <Badge className="bg-zinc-100 text-zinc-600">Out</Badge>}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1.5">
                  {c.is_active ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setEditing(c)} className="h-7 text-xs" data-testid={`edit-${c.id}`}>Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirmDelete(c)} className="h-7 text-xs text-rose-700 hover:text-rose-700 hover:bg-rose-50 border-rose-200" data-testid={`del-${c.id}`}>Remove</Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => restore(c)} className="h-7 text-xs text-emerald-700 hover:text-emerald-700 hover:bg-emerald-50 border-emerald-200" data-testid={`restore-${c.id}`}>Restore</Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
          {filtered.length === 0 && (
            <TableRow><TableCell colSpan={7} className="text-center text-sm text-zinc-500 py-8">No SKUs match your filter.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      {editing && (
        <CatalogEditDialog
          row={editing === "new" ? null : editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged(); }}
        />
      )}

      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Remove this SKU?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">
            <strong>{confirmDelete?.name}</strong> ({confirmDelete?.sku}) will be hidden from the customer shop.
            You can restore it any time.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CatalogEditDialog({ row, categories, onClose, onSaved }) {
  const isNew = !row;
  const [form, setForm] = useState(() => row ? {
    sku: row.sku || "", name: row.name || "", brand: row.brand || "", model: row.model || "",
    category: row.category || "laptop", description: row.description || "",
    image_url: row.image_url || "", price: row.price ?? 0, sla_days: row.sla_days ?? 5,
    in_stock: row.in_stock ?? true, stock_count: row.stock_count ?? "",
    warranty_months: row.warranty_months ?? "",
    tags: (row.tags || []).join(", "),
    spec_highlights: (row.spec_highlights || []).join("\n"),
  } : {
    sku: "", name: "", brand: "", model: "", category: "laptop", description: "",
    image_url: "", price: 0, sla_days: 5, in_stock: true, stock_count: "",
    warranty_months: "", tags: "", spec_highlights: "",
  });
  const [customCat, setCustomCat] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.sku.trim() || !form.name.trim() || !form.category.trim()) {
      toast.error("SKU, name and category are required.");
      return;
    }
    if (!(Number(form.price) > 0)) {
      toast.error("Price must be greater than zero.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        sku: form.sku.trim(), name: form.name.trim(),
        brand: form.brand.trim() || null, model: form.model.trim() || null,
        category: form.category.trim().toLowerCase().replace(/\s+/g, "_"),
        description: form.description.trim() || null,
        image_url: form.image_url.trim() || null,
        price: Number(form.price),
        sla_days: Number(form.sla_days) || 5,
        in_stock: !!form.in_stock,
        stock_count: form.stock_count === "" ? null : Number(form.stock_count),
        warranty_months: form.warranty_months === "" ? null : Number(form.warranty_months),
        tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
        spec_highlights: form.spec_highlights.split("\n").map(t => t.trim()).filter(Boolean),
      };
      if (isNew) {
        await api.post("/tech/catalog", payload);
        toast.success(`Added "${payload.name}"`);
      } else {
        await api.put(`/tech/catalog/${row.id}`, payload);
        toast.success(`Updated "${payload.name}"`);
      }
      onSaved();
    } catch (e) { err(e); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add SKU" : `Edit ${row.sku}`}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <Label className="text-xs">SKU *</Label>
            <Input value={form.sku} onChange={e=>setForm({...form, sku: e.target.value})} placeholder="e.g. LAP-DELL-5440" data-testid="edit-sku"/>
          </div>
          <div>
            <Label className="text-xs">Category *</Label>
            {!customCat ? (
              <Select value={form.category} onValueChange={v => v === "__custom__" ? (setCustomCat(true), setForm({...form, category: ""})) : setForm({...form, category: v})}>
                <SelectTrigger className="text-xs" data-testid="edit-category"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  <SelectItem value="__custom__">+ Add new category…</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="flex gap-1.5">
                <Input autoFocus value={form.category} onChange={e=>setForm({...form, category: e.target.value})} placeholder="new_category" className="text-xs" data-testid="edit-category-new"/>
                <Button size="sm" variant="outline" onClick={() => setCustomCat(false)} className="text-xs h-9">↩</Button>
              </div>
            )}
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Name *</Label>
            <Input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} placeholder="Dell Latitude 5440 i5/16GB/512GB" data-testid="edit-name"/>
          </div>
          <div>
            <Label className="text-xs">Brand</Label>
            <Input value={form.brand} onChange={e=>setForm({...form, brand: e.target.value})} placeholder="Dell"/>
          </div>
          <div>
            <Label className="text-xs">Model</Label>
            <Input value={form.model} onChange={e=>setForm({...form, model: e.target.value})} placeholder="Latitude 5440"/>
          </div>
          <div>
            <Label className="text-xs">Price (₹) *</Label>
            <Input type="number" value={form.price} onChange={e=>setForm({...form, price: e.target.value})} data-testid="edit-price"/>
          </div>
          <div>
            <Label className="text-xs">SLA (days)</Label>
            <Input type="number" value={form.sla_days} onChange={e=>setForm({...form, sla_days: e.target.value})}/>
          </div>
          <div>
            <Label className="text-xs">Stock count</Label>
            <Input type="number" value={form.stock_count} onChange={e=>setForm({...form, stock_count: e.target.value})} placeholder="Optional"/>
          </div>
          <div>
            <Label className="text-xs">Warranty (months)</Label>
            <Input type="number" value={form.warranty_months} onChange={e=>setForm({...form, warranty_months: e.target.value})} placeholder="Optional"/>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Image URL</Label>
            <Input value={form.image_url} onChange={e=>setForm({...form, image_url: e.target.value})} placeholder="https://…"/>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} value={form.description} onChange={e=>setForm({...form, description: e.target.value})} placeholder="Customer-facing one-liner"/>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Spec highlights (one per line)</Label>
            <Textarea rows={3} value={form.spec_highlights} onChange={e=>setForm({...form, spec_highlights: e.target.value})} placeholder={"i5 12th gen\n16GB DDR4\n512GB NVMe SSD"}/>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Tags (comma-separated)</Label>
            <Input value={form.tags} onChange={e=>setForm({...form, tags: e.target.value})} placeholder="developer, laptop, ssd"/>
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <input id="in_stock" type="checkbox" checked={form.in_stock} onChange={e=>setForm({...form, in_stock: e.target.checked})}/>
            <Label htmlFor="in_stock" className="text-xs cursor-pointer">In stock (visible to customers)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="edit-save">
            {busy ? "Saving…" : (isNew ? "Add SKU" : "Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
